/**
 * Estádio procedural (item 3.1): gramado, linhas, areia, arquibancada oval, torcida, torres com bandeiras,
 * aros e entorno. Tudo sai de uma semente e das medidas de `PITCH`, em poucas malhas fundidas ou instanciadas.
 */
import * as THREE from "three";
import { PITCH, SKIN_TONES, hoopCenters, type Kit } from "@besenliga/shared";
import { bakeGroundShadow, bakeUniforms, useBakedShadow } from "./bake.js";
import { PartBuilder, Random, at, ellipseBand, groundRect, merge, toonMaterial } from "./util.js";

export { BAKE_RECT } from "./bake.js";

export { Random } from "./util.js";

export interface StadiumOptions {
  home: Kit;
  away: Kit;
  /** Semente do estádio: mesma semente, mesmo estádio. */
  seed?: number;
  /** Fração dos lugares ocupados, de 0 a 1. */
  occupancy?: number;
  /** Torcida pulando (desligada no preset Baixo). */
  animatedCrowd?: boolean;
  /** Filtro anisotrópico da grama (use `renderer.capabilities.getMaxAnisotropy()`). */
  anisotropy?: number;
  /** Árvores em volta do estádio. */
  trees?: number;
}

export interface Stadium {
  root: THREE.Group;
  /** Avança as animações do cenário (torcida, bandeiras, nuvens). `time` em segundos. */
  update(time: number): void;
  dispose(): void;
  /** Contagem para o orçamento: malhas, instâncias e triângulos. */
  stats(): { meshes: number; instances: number; triangles: number };
  /**
   * Pré-calcula a sombra do cenário no chão (sol vindo de `sunDirection`) e tira o cenário da passada de
   * sombra em tempo real. Chame uma vez, depois de criar o renderer.
   */
  bakeShadows(renderer: THREE.WebGLRenderer, sunDirection: THREE.Vector3, size?: number): void;
}

/** Cores fixas do cenário. As do clube vêm do `Kit`. */
export const STADIUM_COLORS = {
  grassA: "#7cc456",
  grassB: "#6fb74c",
  line: 0xf5f6ee,
  sand: 0xe4c88e,
  plaza: 0xd9d3bf,
  seat: 0xeadfc8,
  seatAlt: 0xdfd2b8,
  concrete: 0xd3c6ab,
  facade: 0xe6dac1,
  rim: 0x2b2f4a,
  towerBody: 0xf0e6d2,
  pole: 0x5b4a3a,
  hoopRing: 0xf2c14e,
  hoopPole: 0xc99a2e,
  hoopBase: 0x8f6b20,
  land: 0x7aa24c,
  hill: 0x6c9a45,
  trunk: 0x6b4a2b,
  foliage: [0x3d7a3a, 0x4f8d3d, 0x467f37],
  walls: [0xf3ead7, 0xe9d9b8, 0xd8e6ea],
  roofs: [0xc0603a, 0xb5532e, 0xd27a45],
  neutralFan: 0x9a8f7f,
  sky: 0x9fd0e6,
  fog: 0xb9dcea,
} as const;

/** Medidas do cenário em frações dos semieixos do gramado (o campo continua em `PITCH`). */
const STAND = {
  /** Perfil da arquibancada: [raio relativo, altura em m], do pé da mureta ao topo e de volta ao chão. */
  wall: 1.1,
  tiers: [
    { r: 1.1, y: 2 },
    { r: 1.14, y: 4 },
    { r: 1.18, y: 6 },
  ],
  back: { r: 1.22, y: 8.5 },
  outer: 1.25,
  segments: 160,
} as const;
const GRASS_TILE_M = 17.5;
const FAN_SPACING_M = 0.9;
/** Torcedores um pouco maiores que o natural, para a torcida ler de longe. */
const FAN_SCALE = 1.3;
const AISLE_EVERY_M = 26;
const AISLE_WIDTH_M = 1.4;

export function buildStadium(opts: StadiumOptions): Stadium {
  const rnd = new Random(opts.seed ?? 2026);
  const root = new THREE.Group();
  root.name = "Estadio";
  const animated: { uniforms: { uTime: THREE.IUniform } }[] = [];
  const disposables: { dispose(): void }[] = [];

  const { pitch, texture } = buildPitch(rnd, opts.anisotropy ?? 8);
  disposables.push(texture);
  const lines = buildLines();
  const ground = buildGround();
  root.add(pitch, lines, ground);
  const receivers = [pitch, ...lines.children, ...ground.children].filter(
    (o): o is THREE.Mesh => o instanceof THREE.Mesh && o.receiveShadow,
  );
  root.add(buildStands(opts));
  const crowd = buildCrowd(rnd, opts);
  animated.push(crowd.material.userData as { uniforms: { uTime: THREE.IUniform } });
  root.add(crowd.mesh);
  root.add(buildTowers(opts));
  const flags = buildFlags(opts);
  animated.push(flags.material.userData as { uniforms: { uTime: THREE.IUniform } });
  root.add(flags.mesh);
  root.add(buildHoops());
  root.add(...buildSurroundings(rnd, opts.trees ?? 280));
  const clouds = buildClouds(rnd);
  root.add(clouds);

  return {
    root,
    update(time) {
      for (const a of animated) if (a.uniforms) a.uniforms.uTime.value = time;
      clouds.rotation.y = time * 0.004;
    },
    dispose() {
      root.traverse((o) => {
        if (!(o instanceof THREE.Mesh)) return;
        o.geometry.dispose();
        for (const m of [o.material].flat() as THREE.Material[]) m.dispose();
      });
      for (const d of disposables) d.dispose();
      root.removeFromParent();
    },
    bakeShadows(renderer, sunDirection, size = 2048) {
      const target = bakeGroundShadow(renderer, root, sunDirection, { size, shadowSize: size * 2 });
      disposables.push(target);
      const uniforms = bakeUniforms();
      uniforms.uBake.value = target.texture;
      for (const m of receivers) for (const mat of [m.material].flat()) useBakedShadow(mat, uniforms);
      // Daqui em diante o cenário não entra mais no mapa de sombra em tempo real.
      root.traverse((o) => {
        o.castShadow = false;
      });
    },
    stats() {
      let meshes = 0;
      let instances = 0;
      let triangles = 0;
      root.traverse((o) => {
        if (!(o instanceof THREE.Mesh)) return;
        meshes++;
        const g = o.geometry;
        const tris = (g.index ? g.index.count : g.getAttribute("position").count) / 3;
        const n = o instanceof THREE.InstancedMesh ? o.count : 1;
        instances += n;
        triangles += tris * n;
      });
      return { meshes, instances, triangles: Math.round(triangles) };
    },
  };
}

// ---------------------------------------------------------------- gramado

function grassTile(rnd: Random): HTMLCanvasElement {
  const size = 512;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d");
  if (!g) throw new Error("Canvas 2D indisponível");
  g.fillStyle = STADIUM_COLORS.grassA;
  g.fillRect(0, 0, size / 2, size);
  g.fillStyle = STADIUM_COLORS.grassB;
  g.fillRect(size / 2, 0, size / 2, size);
  // Grão da grama: pontos claros e escuros, repetíveis porque o ladrilho se repete inteiro.
  for (let i = 0; i < 5200; i++) {
    g.fillStyle = rnd.next() < 0.5 ? "rgba(255,255,255,0.045)" : "rgba(0,40,0,0.06)";
    g.fillRect(Math.floor(rnd.next() * size), Math.floor(rnd.next() * size), 2, 2);
  }
  return c;
}

function buildPitch(rnd: Random, anisotropy: number) {
  const texture = new THREE.CanvasTexture(grassTile(rnd));
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = anisotropy;
  const geo = new THREE.CircleGeometry(1, 160);
  geo.rotateX(-Math.PI / 2);
  geo.scale(PITCH.radiusX, 1, PITCH.radiusZ);
  const pos = geo.getAttribute("position");
  const uv = geo.getAttribute("uv");
  for (let i = 0; i < pos.count; i++) uv.setXY(i, pos.getX(i) / GRASS_TILE_M, pos.getZ(i) / GRASS_TILE_M);
  const pitch = new THREE.Mesh(geo, toonMaterial({ map: texture }));
  pitch.name = "Gramado";
  pitch.receiveShadow = true;
  return { pitch, texture };
}

function buildLines(): THREE.Group {
  const b = new PartBuilder();
  const w = 0.3;
  const { radiusX: rx, radiusZ: rz } = PITCH;
  const id = new THREE.Matrix4();
  const line = (g: THREE.BufferGeometry) => b.add(g, STADIUM_COLORS.line, id, { outline: 0 });
  line(ellipseBand([rx - w, rz - w], [rx + w, rz + w]));
  line(groundRect(-0.25, -rz, 0.25, rz));
  const cc = PITCH.centerCircleRadius;
  line(ellipseBand([cc - 0.25, cc - 0.25], [cc + 0.25, cc + 0.25], { segments: 64 }));
  for (const side of [-1, 1] as const) {
    const cx = side * (PITCH.hoopX - 1);
    const [from, to] = side > 0 ? [Math.PI / 2, Math.PI * 1.5] : [-Math.PI / 2, Math.PI / 2];
    line(
      ellipseBand(
        [PITCH.keeperAreaX - 0.25, PITCH.keeperAreaZ - 0.25],
        [PITCH.keeperAreaX + 0.25, PITCH.keeperAreaZ + 0.25],
        { cx, from, to, segments: 48 },
      ),
    );
    // Marca no chão sob cada aro.
    for (const h of hoopCenters(side))
      line(ellipseBand([0, 0], [0.6, 0.6], { cx: h.x, cz: h.z, segments: 20 }));
  }
  const mat = toonMaterial({
    vertexColors: true,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const g = b.build("Linhas", mat, { castShadow: false, receiveShadow: true });
  g.position.y = 0.01;
  return g;
}

function buildGround(): THREE.Group {
  const b = new PartBuilder();
  const id = new THREE.Matrix4();
  const { radiusX: rx, radiusZ: rz } = PITCH;
  const foot = STAND.wall + 0.002;
  b.add(
    ellipseBand([rx, rz], [rx * foot, rz * foot], { segments: 160, y: -0.005 }),
    STADIUM_COLORS.sand,
    id,
    {
      outline: 0,
    },
  );
  // Esplanada em volta do estádio, onde ficam as torres.
  b.add(
    ellipseBand([rx * (STAND.outer - 0.002), rz * (STAND.outer - 0.002)], [rx * 1.42, rz * 1.46], {
      segments: 160,
      y: -0.02,
    }),
    STADIUM_COLORS.plaza,
    id,
    { outline: 0 },
  );
  const land = new THREE.PlaneGeometry(1400, 1400);
  land.rotateX(-Math.PI / 2);
  b.add(land, STADIUM_COLORS.land, at(0, -0.06, 0), { outline: 0 });
  return b.build("Chao", toonMaterial({ vertexColors: true }), { castShadow: false, receiveShadow: true });
}

// ---------------------------------------------------------------- arquibancada

function buildStands({ home, away }: StadiumOptions): THREE.Mesh {
  type Seg = [number, number, number, number, "wall" | "seat" | "seatAlt" | "concrete" | "rim" | "facade"];
  const t = STAND.tiers;
  const segs: Seg[] = [
    [STAND.wall, 0, t[0].r, t[0].y, "wall"],
    [t[0].r, t[0].y, t[1].r, t[0].y, "seat"],
    [t[1].r, t[0].y, t[1].r, t[1].y, "concrete"],
    [t[1].r, t[1].y, t[2].r, t[1].y, "seatAlt"],
    [t[2].r, t[1].y, t[2].r, t[2].y, "concrete"],
    [t[2].r, t[2].y, STAND.back.r, t[2].y, "seat"],
    [STAND.back.r, t[2].y, STAND.back.r, STAND.back.y, "concrete"],
    [STAND.back.r, STAND.back.y, STAND.outer, STAND.back.y, "rim"],
    [STAND.outer, STAND.back.y, STAND.outer, 0, "facade"],
  ];
  // Pontos duplicados nas quinas: cada degrau tem cor e normal próprias.
  const points = segs.flatMap(([r0, y0, r1, y1]) => [new THREE.Vector2(r0, y0), new THREE.Vector2(r1, y1)]);
  const geo = new THREE.LatheGeometry(points, STAND.segments);
  geo.deleteAttribute("uv");
  geo.scale(PITCH.radiusX, 1, PITCH.radiusZ);
  geo.computeVertexNormals();
  const pos = geo.getAttribute("position");
  const colors = new Float32Array(pos.count * 3);
  const c = new THREE.Color();
  const cHome = new THREE.Color(home.primary);
  const cAway = new THREE.Color(away.primary);
  const fixed: Record<Exclude<Seg[4], "wall">, THREE.Color> = {
    seat: new THREE.Color(STADIUM_COLORS.seat),
    seatAlt: new THREE.Color(STADIUM_COLORS.seatAlt),
    concrete: new THREE.Color(STADIUM_COLORS.concrete),
    rim: new THREE.Color(STADIUM_COLORS.rim),
    facade: new THREE.Color(STADIUM_COLORS.facade),
  };
  for (let i = 0; i < pos.count; i++) {
    const j = i % points.length;
    const kind = segs[Math.floor(j / 2)]?.[4] ?? "concrete";
    if (kind === "wall") {
      // Mureta na cor de cada clube, trocando no meio do campo.
      const k = THREE.MathUtils.smoothstep(pos.getX(i) / PITCH.radiusX, -0.06, 0.06);
      c.copy(cHome).lerp(cAway, k);
    } else c.copy(fixed[kind]);
    c.toArray(colors, i * 3);
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const mesh = new THREE.Mesh(geo, toonMaterial({ vertexColors: true, side: THREE.DoubleSide }));
  mesh.name = "Arquibancada";
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/** Tabela de comprimento de arco de uma elipse, para espalhar pessoas com espaçamento constante. */
function ellipseByArc(rx: number, rz: number, samples = 2048) {
  const acc = new Float64Array(samples + 1);
  let px = rx;
  let pz = 0;
  for (let i = 1; i <= samples; i++) {
    const a = (i / samples) * Math.PI * 2;
    const x = Math.cos(a) * rx;
    const z = Math.sin(a) * rz;
    acc[i] = (acc[i - 1] ?? 0) + Math.hypot(x - px, z - pz);
    px = x;
    pz = z;
  }
  const length = acc[samples] ?? 0;
  /** Ângulo no ponto de comprimento `s` (0 a length). */
  const angleAt = (s: number) => {
    let lo = 0;
    let hi = samples;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if ((acc[mid] ?? 0) < s) lo = mid;
      else hi = mid;
    }
    const a0 = acc[lo] ?? 0;
    const a1 = acc[hi] ?? a0;
    const f = a1 > a0 ? (s - a0) / (a1 - a0) : 0;
    return ((lo + f) / samples) * Math.PI * 2;
  };
  return { length, angleAt };
}

// ---------------------------------------------------------------- torcida

function fanGeometry(): THREE.BufferGeometry {
  const body = new THREE.CylinderGeometry(0.2, 0.27, 0.62, 6).toNonIndexed();
  body.translate(0, 0.31, 0);
  const head = new THREE.IcosahedronGeometry(0.16, 0);
  head.translate(0, 0.8, 0);
  for (const [g, part] of [
    [body, 0],
    [head, 1],
  ] as const) {
    g.deleteAttribute("uv");
    g.setAttribute(
      "aPart",
      new THREE.BufferAttribute(new Float32Array(g.getAttribute("position").count).fill(part), 1),
    );
  }
  return merge([body, head]);
}

function buildCrowd(rnd: Random, { home, away, occupancy = 0.88, animatedCrowd = true }: StadiumOptions) {
  const homeShirts = [home.primary, home.primary, home.primary, home.secondary, home.secondary, 0xfff6e6];
  const awayShirts = [away.primary, away.primary, away.primary, away.secondary, away.secondary, 0xfff6e6];
  const fans: { x: number; y: number; z: number; shirt: number; skin: number }[] = [];
  STAND.tiers.forEach((tier, k) => {
    const next = STAND.tiers[k + 1]?.r ?? STAND.back.r;
    const r = (tier.r + next) / 2;
    const path = ellipseByArc(PITCH.radiusX * r, PITCH.radiusZ * r);
    const count = Math.floor(path.length / FAN_SPACING_M);
    for (let i = 0; i < count; i++) {
      const s = (i + 0.5 * (k % 2)) * FAN_SPACING_M;
      if (s % AISLE_EVERY_M < AISLE_WIDTH_M) continue;
      if (rnd.next() > occupancy) continue;
      const a = path.angleAt(s);
      const x = Math.cos(a) * PITCH.radiusX * r + rnd.range(-0.08, 0.08);
      const z = Math.sin(a) * PITCH.radiusZ * r + rnd.range(-0.08, 0.08);
      // Cada torcida fica do lado do seu time, com mistura perto do meio.
      const homeSide = x + rnd.range(-12, 12) < 0;
      const shirt =
        rnd.next() < 0.08 ? STADIUM_COLORS.neutralFan : rnd.pick(homeSide ? homeShirts : awayShirts);
      fans.push({ x, y: tier.y, z, shirt, skin: rnd.pick(SKIN_TONES).hex });
    }
  });

  const geo = fanGeometry();
  const skin = new Float32Array(fans.length * 3);
  const wave = new Float32Array(fans.length * 2);
  const material = toonMaterial();
  const uniforms = { uTime: { value: 0 }, uJump: { value: animatedCrowd ? 0.32 : 0 } };
  material.userData = { uniforms };
  material.onBeforeCompile = (s) => {
    Object.assign(s.uniforms, uniforms);
    s.vertexShader =
      "attribute float aPart;\nattribute vec3 aSkin;\nattribute vec2 aWave;\nuniform float uTime, uJump;\nvarying float vPart;\nvarying vec3 vSkin;\n" +
      s.vertexShader.replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
        vPart = aPart;
        vSkin = aSkin;
        transformed.y += max(0.0, sin(uTime * aWave.y + aWave.x)) * uJump;`,
      );
    s.fragmentShader =
      "varying float vPart;\nvarying vec3 vSkin;\n" +
      s.fragmentShader.replace(
        "#include <color_fragment>",
        "#include <color_fragment>\n diffuseColor.rgb = mix(diffuseColor.rgb, vSkin, vPart);",
      );
  };
  material.customProgramCacheKey = () => "torcida";
  const mesh = new THREE.InstancedMesh(geo, material, fans.length);
  mesh.name = "Torcida";
  const m = new THREE.Matrix4();
  const c = new THREE.Color();
  fans.forEach((f, i) => {
    const scale = FAN_SCALE * rnd.range(0.92, 1.08);
    mesh.setMatrixAt(
      i,
      m.compose(
        new THREE.Vector3(f.x, f.y, f.z),
        new THREE.Quaternion(),
        new THREE.Vector3(scale, scale, scale),
      ),
    );
    mesh.setColorAt(i, c.setHex(f.shirt));
    c.setHex(f.skin).toArray(skin, i * 3);
    wave[i * 2] = rnd.range(0, Math.PI * 2);
    wave[i * 2 + 1] = rnd.range(2, 5);
  });
  geo.setAttribute("aSkin", new THREE.InstancedBufferAttribute(skin, 3));
  geo.setAttribute("aWave", new THREE.InstancedBufferAttribute(wave, 2));
  mesh.computeBoundingSphere();
  return { mesh, material };
}

// ---------------------------------------------------------------- torres e bandeiras

const TOWER_COUNT = 10;
const TOWER_RING = { x: 1.33, z: 1.36 };

function towerSpots() {
  return Array.from({ length: TOWER_COUNT }, (_, i) => {
    const a = (i / TOWER_COUNT) * Math.PI * 2 + Math.PI / TOWER_COUNT;
    const x = Math.cos(a) * PITCH.radiusX * TOWER_RING.x;
    const z = Math.sin(a) * PITCH.radiusZ * TOWER_RING.z;
    return { a, x, z, home: x < 0 };
  });
}

/** Raio e altura das torres (corpo, faixa, sacada e telhado), para a câmera desviar delas. */
const TOWER_SOLID = { radius: 5.2, top: 30.5 } as const;

/**
 * Altura do que é sólido no estádio no ponto (x, z) do chão: arquibancada pelo perfil e torres (item 4.4, para
 * a câmera não entrar nelas). Fora de tudo, 0. Ignora mastros e bandeiras, que são finos.
 */
export function obstacleHeight(x: number, z: number): number {
  const r = Math.sqrt((x / PITCH.radiusX) ** 2 + (z / PITCH.radiusZ) ** 2);
  let h = 0;
  if (r >= STAND.wall && r <= STAND.outer) {
    const t = STAND.tiers;
    h = r < t[1].r ? t[0].y : r < t[2].r ? t[1].y : r < STAND.back.r ? t[2].y : STAND.back.y;
  }
  for (const t of towerSpots()) {
    const dx = x - t.x;
    const dz = z - t.z;
    if (dx * dx + dz * dz <= TOWER_SOLID.radius * TOWER_SOLID.radius) h = Math.max(h, TOWER_SOLID.top);
  }
  return h;
}

function buildTowers({ home, away }: StadiumOptions): THREE.Group {
  const b = new PartBuilder();
  for (const t of towerSpots()) {
    const kit = t.home ? home : away;
    const place = (g: THREE.BufferGeometry, color: number, y: number, outline = 1.04) =>
      b.add(g, color, at(t.x, y, t.z, [0, -t.a, 0]), { outline });
    place(new THREE.CylinderGeometry(3.4, 3.9, 20, 14), STADIUM_COLORS.towerBody, 10);
    place(new THREE.CylinderGeometry(3.55, 3.55, 3, 14), kit.primary, 16);
    place(new THREE.CylinderGeometry(4.6, 4.2, 4, 14), kit.primary, 21.5);
    place(new THREE.ConeGeometry(5.2, 7, 14), kit.secondary, 27, 1.05);
    place(new THREE.CylinderGeometry(0.14, 0.14, 6, 6), STADIUM_COLORS.pole, 33, 0);
  }
  return b.build("Torres", toonMaterial({ vertexColors: true }));
}

function buildFlags({ home, away }: StadiumOptions) {
  const geo = new THREE.PlaneGeometry(4, 2.4, 10, 1);
  geo.translate(2, 0, 0);
  const material = toonMaterial({ side: THREE.DoubleSide });
  const uniforms = { uTime: { value: 0 } };
  material.userData = { uniforms };
  material.onBeforeCompile = (s) => {
    Object.assign(s.uniforms, uniforms);
    s.vertexShader =
      "uniform float uTime;\n" +
      s.vertexShader.replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
        float k = transformed.x / 4.0;
        transformed.z += sin(uTime * 3.2 + transformed.x * 1.4 + float(gl_InstanceID) * 1.7) * 0.45 * k;`,
      );
  };
  material.customProgramCacheKey = () => "bandeira";
  const spots = towerSpots();
  const mesh = new THREE.InstancedMesh(geo, material, spots.length);
  mesh.name = "Bandeiras";
  mesh.castShadow = true;
  const c = new THREE.Color();
  spots.forEach((t, i) => {
    const kit = t.home ? home : away;
    // Bandeira na cor principal: contrasta com o telhado (secundária) e com o céu.
    mesh.setMatrixAt(i, at(t.x, 34.6, t.z, [0, -t.a + 0.6, 0]));
    mesh.setColorAt(i, c.setHex(kit.primary));
  });
  mesh.computeBoundingSphere();
  return { mesh, material };
}

// ---------------------------------------------------------------- aros

function buildHoops(): THREE.Group {
  const b = new PartBuilder();
  for (const side of [-1, 1] as const) {
    for (const h of hoopCenters(side)) {
      const poleH = h.y - PITCH.hoopRadius;
      b.add(
        new THREE.CylinderGeometry(0.35, 0.45, poleH, 10),
        STADIUM_COLORS.hoopPole,
        at(h.x, poleH / 2, h.z),
        {
          outline: 1.12,
        },
      );
      b.add(new THREE.CylinderGeometry(1.4, 1.8, 0.8, 12), STADIUM_COLORS.hoopBase, at(h.x, 0.4, h.z), {
        radial: true,
      });
      b.add(
        new THREE.TorusGeometry(PITCH.hoopRadius, 0.32, 10, 36),
        STADIUM_COLORS.hoopRing,
        at(h.x, h.y, h.z, [0, Math.PI / 2, 0]),
        { outline: 1.1 },
      );
    }
  }
  return b.build("Aros", toonMaterial({ vertexColors: true }));
}

// ---------------------------------------------------------------- entorno

function buildSurroundings(rnd: Random, treeCount: number): THREE.Object3D[] {
  // Árvores: copa de dois cones e tronco, instanciados.
  const crownLow = new THREE.ConeGeometry(3, 6, 7);
  crownLow.translate(0, 5.2, 0);
  const crownTop = new THREE.ConeGeometry(2.2, 5, 7);
  crownTop.translate(0, 8.2, 0);
  for (const [g, shade] of [
    [crownLow, 1],
    [crownTop, 1.12],
  ] as const) {
    g.deleteAttribute("uv");
    const n = g.getAttribute("position").count;
    g.setAttribute("color", new THREE.BufferAttribute(new Float32Array(n * 3).fill(shade), 3));
  }
  const crown = new THREE.InstancedMesh(
    merge([crownLow, crownTop]),
    toonMaterial({ vertexColors: true }),
    treeCount,
  );
  crown.name = "Arvores";
  crown.castShadow = true;
  const trunkGeo = new THREE.CylinderGeometry(0.5, 0.6, 2.4, 6);
  trunkGeo.translate(0, 1.2, 0);
  const trunk = new THREE.InstancedMesh(trunkGeo, toonMaterial({ color: STADIUM_COLORS.trunk }), treeCount);
  trunk.name = "Troncos";
  const c = new THREE.Color();
  for (let i = 0; i < treeCount; i++) {
    let x = 0;
    let z = 0;
    do {
      x = rnd.range(-360, 360);
      z = rnd.range(-300, 300);
    } while ((x / 118) ** 2 + (z / 86) ** 2 < 1);
    const s = rnd.range(0.75, 1.7);
    const m = at(x, 0, z, [0, rnd.range(0, Math.PI * 2), 0], s);
    crown.setMatrixAt(i, m);
    trunk.setMatrixAt(i, m);
    crown.setColorAt(i, c.setHex(rnd.pick(STADIUM_COLORS.foliage)));
  }
  crown.computeBoundingSphere();
  trunk.computeBoundingSphere();

  // Casas espalhadas e morros no horizonte, fundidos.
  const houses = new PartBuilder();
  for (let i = 0; i < 22; i++) {
    const a = rnd.range(0, Math.PI * 2);
    const r = rnd.range(150, 250);
    const x = Math.cos(a) * r * 1.3;
    const z = Math.sin(a) * r;
    const turn = rnd.range(0, Math.PI * 2);
    const wide = rnd.range(0.85, 1.25);
    houses.add(
      new THREE.BoxGeometry(8, 5, 7),
      STADIUM_COLORS.walls[i % 3] ?? 0xffffff,
      at(x, 2.5, z, [0, turn, 0], [wide, 1, 1]),
      {
        outline: 1.04,
      },
    );
    houses.add(
      new THREE.ConeGeometry(6.4, 4, 4),
      STADIUM_COLORS.roofs[i % 3] ?? 0xc0603a,
      at(x, 7, z, [0, turn + Math.PI / 4, 0], [wide, 1, 1]),
      { outline: 1.05 },
    );
  }
  const hills = new PartBuilder();
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + 0.4;
    hills.add(
      new THREE.SphereGeometry(60, 18, 12),
      STADIUM_COLORS.hill,
      at(Math.cos(a) * 430, -6, Math.sin(a) * 370, [0, 0, 0], [1.6 * rnd.range(0.85, 1.2), 0.35, 1.2]),
      { outline: 0 },
    );
  }
  return [
    crown,
    trunk,
    houses.build("Casas", toonMaterial({ vertexColors: true })),
    hills.build("Morros", toonMaterial({ vertexColors: true }), { castShadow: false, receiveShadow: true }),
  ];
}

function buildClouds(rnd: Random): THREE.Group {
  const b = new PartBuilder();
  for (let i = 0; i < 10; i++) {
    const a = rnd.range(0, Math.PI * 2);
    const r = rnd.range(150, 280);
    const cx = Math.cos(a) * r;
    const cz = Math.sin(a) * r;
    const cy = rnd.range(42, 56);
    for (let j = 0; j < 5; j++) {
      const size = rnd.range(6, 10);
      b.add(
        new THREE.IcosahedronGeometry(size, 1),
        0xffffff,
        at(
          cx + (j * 7 - 14) * Math.cos(a + 1.57),
          cy + rnd.range(0, 3),
          cz + (j * 7 - 14) * Math.sin(a + 1.57),
          [0, 0, 0],
          [1, 0.6, 1],
        ),
        { outline: 0 },
      );
    }
  }
  return b.build("Nuvens", toonMaterial({ vertexColors: true, emissive: 0x9fb8c8 }), { castShadow: false });
}

// ---------------------------------------------------------------- luz

/** Direção do sol de fim de tarde (do chão para o sol). */
export const SUN_DIRECTION = new THREE.Vector3(70, 110, 45).normalize();
/** Meia largura da área coberta pela sombra em tempo real, centrada na jogada (m). */
export const LIVE_SHADOW_HALF = 45;

/**
 * Céu, neblina e sol de fim de tarde da cena de referência. A sombra em tempo real cobre só a área da jogada
 * (`followSun`); a do cenário vem de `Stadium.bakeShadows`.
 */
export function addStadiumLights(scene: THREE.Scene, { shadowSize = 2048 } = {}): THREE.DirectionalLight {
  scene.background = new THREE.Color(STADIUM_COLORS.sky);
  scene.fog = new THREE.Fog(STADIUM_COLORS.fog, 260, 620);
  scene.add(new THREE.HemisphereLight(0xcfe9ff, 0x5f7f35, 1.7));
  const sun = new THREE.DirectionalLight(0xffe0b0, 3.2);
  sun.castShadow = shadowSize > 0;
  sun.shadow.mapSize.set(shadowSize, shadowSize);
  const h = LIVE_SHADOW_HALF;
  Object.assign(sun.shadow.camera, { left: -h, right: h, top: h, bottom: -h, near: 1, far: 400 });
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.02;
  scene.add(sun, sun.target);
  followSun(sun, new THREE.Vector3());
  return sun;
}

/**
 * Move o sol (e a área da sombra em tempo real) para acompanhar o ponto `focus`, sem mudar a direção.
 * `half` é a meia largura da área: pequena na câmera de jogo, maior nas câmeras que mostram o campo todo.
 */
export function followSun(sun: THREE.DirectionalLight, focus: THREE.Vector3, half = LIVE_SHADOW_HALF): void {
  const cam = sun.shadow.camera;
  if (cam.right !== half) {
    Object.assign(cam, { left: -half, right: half, top: half, bottom: -half });
    cam.updateProjectionMatrix();
  }
  sun.target.position.copy(focus).setY(0);
  sun.position.copy(sun.target.position).addScaledVector(SUN_DIRECTION, 180);
  sun.target.updateMatrixWorld();
}
